import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { customToast } from '../utils/customToast';
import { supportTicketService } from '../services/supportTicketService';
import ConfirmationModal from '../components/ConfirmationModal';
import { ArrowLeft, Send, MessageSquare, AlertCircle, Clock, FileText, Download, User, Shield, X, ChevronLeft, ChevronRight, RotateCw, Paperclip, Trash2, Lock, Unlock, CheckCircle2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface SupportTicket {
  id: number;
  shop_id?: number;
  subject: string;
  description: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  attachment_url?: string;
  created_at: string;
  updated_at: string;
  shop?: {
    id: number;
    shop_name: string;
  };
  replies?: any[];
}

const TicketDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user } = useAuth();

  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  const [sendingReply, setSendingReply] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [selectedReplyImageIndex, setSelectedReplyImageIndex] = useState<number | null>(null);
  const [selectedReplyImages, setSelectedReplyImages] = useState<string[]>([]);
  const repliesContainerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTicket();
  }, [id]);

  useEffect(() => {
    if (ticket?.attachment_url) {
      let urls: string[] = [];
      try {
        const parsed = JSON.parse(ticket.attachment_url);
        urls = Array.isArray(parsed) ? parsed : [ticket.attachment_url];
      } catch {
        urls = [ticket.attachment_url];
      }
      setImageUrls(urls);
    }
  }, [ticket?.attachment_url]);

  // Auto-scroll to bottom when ticket loads or replies change
  useEffect(() => {
    if (repliesContainerRef.current) {
      setTimeout(() => {
        if (repliesContainerRef.current) {
          repliesContainerRef.current.scrollTop = repliesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [ticket?.replies]);

  // Manage body overflow when lightbox is open
  useEffect(() => {
    if (selectedImageIndex !== null || selectedReplyImageIndex !== null) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [selectedImageIndex, selectedReplyImageIndex]);

  const loadTicket = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const data = await supportTicketService.getTicket(parseInt(id));
      setTicket(data);
    } catch (error) {
      console.error('Failed to load ticket:', error);
      customToast.error('Failed to load ticket details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !ticket) {
      customToast.error('Please enter a message');
      return;
    }

    try {
      setSendingReply(true);

      // Create FormData to handle file attachments
      const formData = new FormData();
      formData.append('message', replyMessage);

      // Append reply attachments if any
      replyAttachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });

      await supportTicketService.addReplyWithAttachment(ticket.id, formData);
      customToast.success('Reply sent successfully');
      setReplyMessage('');
      setReplyAttachments([]);
      await loadTicket();
    } catch (error) {
      console.error('Failed to send reply:', error);
      customToast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!ticket) return;

    if (newStatus === 'closed') {
      setShowCloseConfirmation(true);
      setShowStatusDropdown(false);
      return;
    }

    try {
      setUpdatingStatus(true);
      await supportTicketService.updateTicketStatus(ticket.id, newStatus);
      customToast.success('Ticket status updated');
      setShowStatusDropdown(false);
      await loadTicket();
    } catch (error) {
      console.error('Failed to update status:', error);
      customToast.error('Failed to update ticket status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const confirmCloseTicket = async () => {
    if (!ticket) return;

    try {
      setUpdatingStatus(true);
      await supportTicketService.closeTicket(ticket.id);
      customToast.success('Ticket closed');
      await loadTicket();
    } catch (error) {
      console.error('Failed to close ticket:', error);
      customToast.error('Failed to close ticket');
    } finally {
      setUpdatingStatus(false);
      setShowCloseConfirmation(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return theme === 'dark' ? 'bg-red-900/20 text-red-400 border-red-500/30' : 'bg-red-50 text-red-700 border-red-200';
      case 'high':
        return theme === 'dark' ? 'bg-orange-900/20 text-orange-400 border-orange-500/30' : 'bg-orange-50 text-orange-700 border-orange-200';
      case 'medium':
        return theme === 'dark' ? 'bg-yellow-900/20 text-yellow-400 border-yellow-500/30' : 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default:
        return theme === 'dark' ? 'bg-blue-900/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-700 border-blue-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return theme === 'dark' ? 'bg-blue-900/20 text-blue-400 border-blue-500/30' : 'bg-blue-50 text-blue-700 border-blue-200';
      case 'in-progress':
        return theme === 'dark' ? 'bg-purple-900/20 text-purple-400 border-purple-500/30' : 'bg-purple-50 text-purple-700 border-purple-200';
      case 'resolved':
        return theme === 'dark' ? 'bg-indigo-900/20 text-indigo-400 border-indigo-500/30' : 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case 'closed':
        return theme === 'dark' ? 'bg-gray-900/20 text-gray-400 border-gray-500/30' : 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen p-4 sm:p-6 ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50'
        }`}>
        <div className="max-w-5xl mx-auto space-y-4 sm:space-y-6">
          {/* Header Skeleton */}
          <div className="flex flex-col space-y-2 sm:space-y-3">
            <div className={`h-8 w-32 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
              }`} />
            <div className={`h-4 w-48 rounded-lg animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
              }`} />
          </div>

          {/* Details Card Skeleton */}
          <div className={`rounded-xl border p-4 sm:p-6 space-y-4 animate-pulse ${theme === 'dark'
            ? 'bg-slate-800/50 border-slate-700'
            : 'bg-white border-gray-200'
            }`}>
            {[1, 2, 3, 4].map((item) => (
              <div key={item} className="space-y-2">
                <div className={`h-4 w-20 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                  }`} />
                <div className={`h-6 w-full rounded-lg ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                  }`} />
              </div>
            ))}
          </div>

          {/* Timeline Skeleton */}
          <div className="grid grid-cols-2 gap-4">
            {[1, 2].map((item) => (
              <div
                key={item}
                className={`rounded-lg p-4 space-y-2 animate-pulse ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-white'
                  }`}
              >
                <div className={`h-4 w-16 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                  }`} />
                <div className={`h-5 w-32 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                  }`} />
                <div className={`h-3 w-24 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                  }`} />
              </div>
            ))}
          </div>

          {/* Attachments Skeleton */}
          <div className={`rounded-lg p-4 space-y-3 animate-pulse ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-white'
            }`}>
            <div className={`h-4 w-20 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
              }`} />
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className={`h-20 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                    }`}
                />
              ))}
            </div>
          </div>

          {/* Summary Table Skeleton */}
          <div className={`rounded-xl border overflow-hidden animate-pulse ${theme === 'dark'
            ? 'bg-slate-800/50 border-slate-700'
            : 'bg-white border-gray-200'
            }`}>
            <div className={`px-4 sm:px-6 py-3 border-b ${theme === 'dark' ? 'bg-slate-700/50 border-slate-700' : 'bg-gray-50 border-gray-200'
              }`}>
              <div className={`h-4 w-24 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                }`} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4">
              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className={`px-4 py-4 border-r space-y-2 ${theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
                    }`}
                >
                  <div className={`h-3 w-12 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                    }`} />
                  <div className={`h-5 w-20 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                    }`} />
                </div>
              ))}
            </div>
          </div>

          {/* Replies Skeleton */}
          <div className={`rounded-lg space-y-3 ${theme === 'dark' ? 'bg-slate-800/50' : 'bg-white'
            }`}>
            <div className={`h-5 w-32 rounded animate-pulse ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
              }`} />
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className={`rounded-lg p-4 space-y-3 animate-pulse border ${theme === 'dark'
                  ? 'bg-slate-700/50 border-slate-700'
                  : 'bg-gray-50 border-gray-200'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <div className={`h-4 w-24 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                    }`} />
                  <div className={`h-3 w-20 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                    }`} />
                </div>
                <div className="space-y-2">
                  <div className={`h-4 w-full rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                    }`} />
                  <div className={`h-4 w-5/6 rounded ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-300'
                    }`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className={`min-h-screen p-6 ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50'
        }`}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Ticket not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Lightbox Modals - Outside main container to cover full screen */}
      {/* Image Lightbox Modal */}
      {selectedImageIndex !== null && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/80 z-[9999] flex items-center justify-center p-4" onClick={() => setSelectedImageIndex(null)}>
          <div className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center" onClick={e => e.stopPropagation()}>
            <img
              src={imageUrls[selectedImageIndex].startsWith('http') ? imageUrls[selectedImageIndex] : `${import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || 'http://localhost:8000'}${imageUrls[selectedImageIndex]}`}
              alt="Full size"
              className="w-full h-auto rounded-lg max-h-[80vh] object-contain"
            />
            <button
              onClick={() => setSelectedImageIndex(null)}
              className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
            {imageUrls.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedImageIndex((selectedImageIndex - 1 + imageUrls.length) % imageUrls.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => setSelectedImageIndex((selectedImageIndex + 1) % imageUrls.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-lg text-sm">
                  {selectedImageIndex + 1} / {imageUrls.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reply Image Lightbox Modal */}
      {selectedReplyImageIndex !== null && selectedReplyImages.length > 0 && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/80 z-[9999] flex items-center justify-center p-4" onClick={() => setSelectedReplyImageIndex(null)}>
          <div className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center" onClick={e => e.stopPropagation()}>
            <img
              src={selectedReplyImages[selectedReplyImageIndex]}
              alt="Full size"
              className="w-full h-auto rounded-lg max-h-[80vh] object-contain"
            />
            <button
              onClick={() => setSelectedReplyImageIndex(null)}
              className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
            {selectedReplyImages.length > 1 && (
              <>
                <button
                  onClick={() => setSelectedReplyImageIndex((selectedReplyImageIndex - 1 + selectedReplyImages.length) % selectedReplyImages.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => setSelectedReplyImageIndex((selectedReplyImageIndex + 1) % selectedReplyImages.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-lg transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-lg text-sm">
                  {selectedReplyImageIndex + 1} / {selectedReplyImages.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className={`min-h-screen p-4 sm:p-6 ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50'
        }`}>
        <ConfirmationModal
          isOpen={showCloseConfirmation}
          onClose={() => setShowCloseConfirmation(false)}
          onConfirm={confirmCloseTicket}
          title="Close Ticket"
          message="Are you sure you want to close this ticket? This action cannot be undone."
          confirmText="Close Ticket"
          cancelText="Cancel"
          type="danger"
          isLoading={updatingStatus}
        />
        {/* Header with Navigation */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => navigate(-1)}
              className={`inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${theme === 'dark'
                ? 'text-blue-400 hover:bg-slate-800'
                : 'text-blue-600 hover:bg-blue-50'
                }`}
            >
              <ArrowLeft size={16} />
              Go Back
            </button>

            <button
              onClick={() => loadTicket()}
              disabled={loading}
              className={`inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors ${loading
                ? theme === 'dark'
                  ? 'text-gray-500 cursor-not-allowed'
                  : 'text-gray-400 cursor-not-allowed'
                : theme === 'dark'
                  ? 'text-blue-400 hover:bg-slate-800'
                  : 'text-blue-600 hover:bg-blue-50'
                }`}
              title="Refresh ticket details"
            >
              <RotateCw size={16} className={loading ? 'animate-spin' : ''} />
              {!loading && 'Refresh'}
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className={`text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                Ticket #{ticket.id}
              </p>
              <h1 className={`text-2xl sm:text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                {ticket.subject}
              </h1>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6">
          {/* Main Content */}
          <div className="space-y-6">
            {/* Summary Card - Horizontal Table Layout - MOVED TO TOP */}
            <div className={`rounded-xl border shadow-sm overflow-visible ${theme === 'dark'
              ? 'bg-slate-800/50 border-slate-700'
              : 'bg-white border-gray-200'
              }`}>
              <div className={`px-4 sm:px-6 py-3 border-b ${theme === 'dark' ? 'bg-slate-700/50 border-slate-700' : 'bg-gray-50 border-gray-200'
                }`}>
                <h3 className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                  Ticket Summary
                </h3>
              </div>
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-x divide-y sm:divide-y-0" style={{
                borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
              }}>
                {/* Status - with Icon and Dropdown */}
                <div className={`px-4 py-4 ${theme === 'dark' ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                  } relative`}>
                  <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                    Status
                  </p>
                  {user?.role === 'super_admin' ? (
                    <div className="relative">
                      <button
                        onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                        disabled={updatingStatus}
                        className={`flex items-center gap-2 px-2 py-1 rounded text-xs font-medium border transition-colors ${getStatusColor(ticket.status)
                          } ${updatingStatus ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                      >
                        {ticket.status === 'open' && <Unlock size={14} />}
                        {ticket.status === 'in-progress' && <Clock size={14} />}
                        {ticket.status === 'resolved' && <CheckCircle2 size={14} />}
                        {ticket.status === 'closed' && <Lock size={14} />}
                        <span>
                          {ticket.status === 'in-progress' ? 'In Progress' : ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                        </span>
                      </button>

                      {showStatusDropdown && (
                        <div className={`absolute top-full left-0 mt-1 rounded-lg border shadow-lg z-[9999] min-w-[150px] ${theme === 'dark'
                          ? 'bg-slate-800 border-slate-700'
                          : 'bg-white border-gray-200'
                          }`}>
                          {['open', 'in-progress', 'resolved', 'closed'].map((status) => (
                            <button
                              key={status}
                              onClick={() => handleStatusChange(status)}
                              disabled={updatingStatus}
                              className={`w-full px-3 py-2 text-xs font-medium text-left transition-colors first:rounded-t-lg last:rounded-b-lg flex items-center gap-2 ${ticket.status === status
                                ? theme === 'dark'
                                  ? 'bg-blue-600/20 text-blue-400'
                                  : 'bg-blue-50 text-blue-700'
                                : theme === 'dark'
                                  ? 'text-gray-300 hover:bg-slate-700/50'
                                  : 'text-gray-700 hover:bg-gray-50'
                                } ${updatingStatus ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            >
                              {status === 'open' && <Unlock size={12} />}
                              {status === 'in-progress' && <Clock size={12} />}
                              {status === 'resolved' && <CheckCircle2 size={12} />}
                              {status === 'closed' && <Lock size={12} />}
                              <span>
                                {status === 'in-progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      {ticket.status === 'open' && <Unlock size={16} className="text-blue-500" />}
                      {ticket.status === 'in-progress' && <Clock size={16} className="text-purple-500" />}
                      {ticket.status === 'resolved' && <CheckCircle2 size={16} className="text-green-500" />}
                      {ticket.status === 'closed' && <Lock size={16} className="text-gray-500" />}
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${getStatusColor(ticket.status)}`}>
                        {ticket.status === 'in-progress' ? 'In Progress' : ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Priority */}
                <div className={`px-4 py-4 ${theme === 'dark' ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                  }`}>
                  <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                    Priority
                  </p>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                  </span>
                </div>

                {/* Category */}
                <div className={`px-4 py-4 ${theme === 'dark' ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                  }`}>
                  <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                    Category
                  </p>
                  <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                    {ticket.category.charAt(0).toUpperCase() + ticket.category.slice(1).replace('-', ' ')}
                  </p>
                </div>

                {/* Replies */}
                <div className={`px-4 py-4 ${theme === 'dark' ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                  }`}>
                  <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                    Replies
                  </p>
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                    }`}>
                    {ticket.replies?.length || 0}
                  </p>
                </div>

                {/* Last Response */}
                <div className={`px-4 py-4 ${theme === 'dark' ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'
                  }`}>
                  <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                    Last Response
                  </p>
                  <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                    {ticket.replies && ticket.replies.length > 0
                      ? new Date(ticket.replies[0].created_at).toLocaleDateString()
                      : 'None yet'}
                  </p>
                </div>
              </div>
            </div>

            {/* Ticket Details Card */}
            <div className={`rounded-xl border shadow-sm ${theme === 'dark'
              ? 'bg-slate-800/50 border-slate-700'
              : 'bg-white border-gray-200'
              }`}>
              <div className={`px-4 sm:px-6 py-4 border-b ${theme === 'dark' ? 'bg-slate-700/50 border-slate-700' : 'bg-gray-50 border-gray-200'
                }`}>
                <h2 className={`text-base sm:text-lg font-semibold flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                  <FileText size={18} className="text-blue-600" />
                  Details
                </h2>
              </div>

              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                {/* Category & Shop */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}>
                    <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                      Category
                    </p>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                      }`}>
                      {ticket.category.charAt(0).toUpperCase() + ticket.category.slice(1).replace('-', ' ')}
                    </p>
                  </div>
                  <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}>
                    <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                      Shop
                    </p>
                    <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                      }`}>
                      {ticket.shop?.shop_name || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <p className={`text-xs font-medium mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                    Description
                  </p>
                  <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}>
                    <p className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                      {ticket.description}
                    </p>
                  </div>
                </div>

                {/* Attachments - Compact Gallery */}
                {ticket.attachment_url && (() => {
                  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || 'http://localhost:8000';

                  const images = imageUrls.filter(url => url.match(/\.(jpg|jpeg|png|gif|webp)$/i));
                  const nonImages = imageUrls.filter(url => !url.match(/\.(jpg|jpeg|png|gif|webp)$/i));

                  return (
                    <div>
                      <p className={`text-xs font-medium mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Attachments ({imageUrls.length})
                      </p>
                      <div className={`rounded-lg p-4 ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                        {images.length > 0 && (
                          <div className="mb-4">
                            <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Images</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {images.map((url, index) => {
                                const fullUrl = url.startsWith('http') ? url : `${apiBaseUrl}${url}`;
                                console.log('🖼️ Ticket attachment image URL:', {
                                  original: url,
                                  fullUrl,
                                  apiBaseUrl,
                                  index
                                });
                                return (
                                  <button
                                    key={index}
                                    onClick={() => setSelectedImageIndex(index)}
                                    className="relative group rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all"
                                  >
                                    <img
                                      src={fullUrl}
                                      alt={`Attachment ${index + 1}`}
                                      className="w-full h-20 object-cover group-hover:brightness-75 transition-all"
                                      onError={(e) => {
                                        console.error('❌ Error loading ticket attachment image:', {
                                          url,
                                          fullUrl,
                                          apiBaseUrl,
                                          error: e
                                        });
                                      }}
                                      onLoad={() => {
                                        console.log('✅ Successfully loaded ticket attachment image:', fullUrl);
                                      }}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50">
                                      <span className="text-white text-xs font-medium">View</span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {nonImages.length > 0 && (
                          <div>
                            <p className={`text-xs font-medium mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Files</p>
                            <div className="space-y-2">
                              {nonImages.map((url, index) => {
                                const fullUrl = url.startsWith('http') ? url : `${apiBaseUrl}${url}`;
                                return (
                                  <a
                                    key={index}
                                    href={fullUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg transition-colors ${theme === 'dark'
                                      ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30'
                                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                      }`}
                                  >
                                    <Download size={14} />
                                    Download
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Timeline */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className={`rounded-lg p-4 flex items-start gap-3 ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}>
                    <Clock size={16} className={`mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`} />
                    <div>
                      <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                        Created
                      </p>
                      <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                        }`}>
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </p>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                        {new Date(ticket.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <div className={`rounded-lg p-4 flex items-start gap-3 ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-gray-50'
                    }`}>
                    <Clock size={16} className={`mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`} />
                    <div>
                      <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                        Updated
                      </p>
                      <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                        }`}>
                        {new Date(ticket.updated_at).toLocaleDateString()}
                      </p>
                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                        {new Date(ticket.updated_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Conversation */}
              <div className={`rounded-xl border shadow-sm overflow-hidden ${theme === 'dark'
                ? 'bg-slate-800/50 border-slate-700'
                : 'bg-white border-gray-200'
                }`}>
                <div className={`px-4 sm:px-6 py-4 border-b ${theme === 'dark' ? 'bg-slate-700/50 border-slate-700' : 'bg-gray-50 border-gray-200'
                  }`}>
                  <h2 className={`text-base sm:text-lg font-semibold flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                    <MessageSquare size={18} className="text-blue-500" />
                    Conversation
                    <span className={`ml-auto text-xs sm:text-sm font-normal ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                      {ticket.replies?.length || 0} replies
                    </span>
                  </h2>
                </div>

                {/* Replies */}
                <div
                  ref={repliesContainerRef}
                  className={`space-y-3 p-4 sm:p-6 max-h-[50vh] sm:max-h-96 overflow-y-auto rounded-lg ${theme === 'dark' ? 'bg-slate-900/50' : 'bg-gray-50'
                    }`}>
                  {!ticket.replies || ticket.replies.length === 0 ? (
                    <p className={`text-sm text-center py-8 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                      }`}>
                      No replies yet. Start the conversation below.
                    </p>
                  ) : (
                    ticket.replies.map((reply: any) => (
                      <div
                        key={reply.id}
                        className={`p-4 rounded-lg border ${reply.is_admin
                          ? theme === 'dark'
                            ? 'bg-blue-900/20 border-blue-500/30 shadow-sm'
                            : 'bg-blue-50 border-blue-200 shadow-sm'
                          : theme === 'dark'
                            ? 'bg-slate-700/50 border-slate-600'
                            : 'bg-white border-gray-200'
                          }`}
                      >
                        <div className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-3`}>
                          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 sm:px-3 py-1.5 rounded-lg w-fit ${reply.is_admin
                            ? theme === 'dark'
                              ? 'bg-blue-600/30 text-blue-300'
                              : 'bg-blue-100 text-blue-700'
                            : theme === 'dark'
                              ? 'bg-slate-600/30 text-gray-300'
                              : 'bg-gray-100 text-gray-700'
                            }`}>
                            {reply.is_admin ? (
                              <>
                                <Shield size={14} />
                                {reply.user?.full_name || 'Support Staff'}
                              </>
                            ) : (
                              <>
                                <User size={14} />
                                {ticket.shop?.shop_name || 'Shop Owner'}
                              </>
                            )}
                          </span>
                          <span className={`text-xs sm:ml-auto ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                            }`}>
                            {new Date(reply.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className={`text-sm leading-relaxed prose prose-sm max-w-none ${theme === 'dark'
                          ? 'text-gray-200 prose-invert [&_strong]:text-gray-100 [&_em]:text-gray-100 [&_a]:text-blue-400 [&_a:hover]:text-blue-300 [&_blockquote]:border-l-blue-400 [&_blockquote]:text-gray-300 [&_code]:text-orange-300 [&_code]:bg-slate-700 [&_ol]:text-gray-200 [&_ul]:text-gray-200 [&_li]:text-gray-200'
                          : 'text-gray-800 [&_strong]:font-bold [&_a]:text-blue-600 [&_a:hover]:text-blue-700 [&_blockquote]:border-l-blue-500 [&_blockquote]:text-gray-700 [&_code]:text-orange-600 [&_code]:bg-gray-100 [&_ol]:text-gray-800 [&_ul]:text-gray-800 [&_li]:text-gray-800'
                          }`}>
                          <div dangerouslySetInnerHTML={{ __html: reply.message }} />
                        </div>

                        {/* Reply Attachments */}
                        {reply.attachment_url && (
                          <div className="mt-4 space-y-2">
                            {(() => {
                              const apiBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api', '') || 'http://localhost:8000';
                              let attachmentUrls: string[] = [];

                              if (typeof reply.attachment_url === 'string') {
                                try {
                                  attachmentUrls = JSON.parse(reply.attachment_url);
                                } catch {
                                  attachmentUrls = [reply.attachment_url];
                                }
                              } else if (Array.isArray(reply.attachment_url)) {
                                attachmentUrls = reply.attachment_url;
                              }

                              const images = attachmentUrls.filter(url => url.match(/\.(jpg|jpeg|png|gif|webp)$/i));
                              const nonImages = attachmentUrls.filter(url => !url.match(/\.(jpg|jpeg|png|gif|webp)$/i));

                              return attachmentUrls.length > 0 ? (
                                <div className="space-y-3">
                                  <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                    Attachments
                                  </p>
                                  {images.length > 0 && (
                                    <div className="space-y-2">
                                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                        }`}>Images</p>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                        {images.map((url, idx) => {
                                          const fullUrl = url.startsWith('http') ? url : `${apiBaseUrl}${url}`;
                                          const fileName = url.split('/').pop() || 'file';

                                          return (
                                            <button
                                              key={idx}
                                              onClick={() => {
                                                setSelectedReplyImages(images.map(img => img.startsWith('http') ? img : `${apiBaseUrl}${img}`));
                                                setSelectedReplyImageIndex(idx);
                                              }}
                                              className="relative group rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all"
                                            >
                                              <img
                                                src={fullUrl}
                                                alt={fileName}
                                                className="w-full h-20 object-cover group-hover:brightness-75 transition-all"
                                                onError={() => console.log('Error loading image:', fullUrl)}
                                              />
                                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/50">
                                                <span className="text-white text-xs font-medium">View</span>
                                              </div>
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                  {nonImages.length > 0 && (
                                    <div className="space-y-2">
                                      <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                        }`}>Files</p>
                                      <div className="space-y-2">
                                        {nonImages.map((url, idx) => {
                                          const fullUrl = url.startsWith('http') ? url : `${apiBaseUrl}${url}`;
                                          const fileName = url.split('/').pop() || 'file';

                                          return (
                                            <a
                                              key={idx}
                                              href={fullUrl}
                                              download
                                              className={`inline-flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg transition-colors ${theme === 'dark'
                                                ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30'
                                                : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                                }`}
                                            >
                                              <Download size={14} />
                                              {fileName}
                                            </a>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : null;
                            })()}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Reply Input */}
                <div className="border-t p-4 sm:p-6 space-y-3">
                  {/* Rich Text Editor */}
                  <div className={`rounded-lg border overflow-hidden ${theme === 'dark'
                    ? 'border-slate-600 [&_.ql-container]:bg-slate-700/50 [&_.ql-editor]:text-white [&_.ql-editor.ql-blank::before]:text-gray-500 [&_.ql-toolbar]:bg-slate-700/30 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-slate-600'
                    : 'border-gray-300 [&_.ql-container]:bg-white'
                    }`}>
                    <ReactQuill
                      theme="snow"
                      value={replyMessage}
                      onChange={setReplyMessage}
                      placeholder="Type your reply..."
                      modules={{
                        toolbar: [
                          ['bold', 'italic', 'underline', 'strike'],
                          ['blockquote', 'code-block'],
                          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                          [{ 'color': [] }, { 'background': [] }],
                          ['link'],
                          ['clean']
                        ]
                      }}
                      formats={['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block', 'list', 'color', 'background', 'link']}
                    />
                  </div>

                  {/* File Attachment Section */}
                  <label className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${theme === 'dark'
                    ? 'border-slate-600 hover:border-blue-500 hover:bg-slate-700/30'
                    : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                    }`}>
                    <Paperclip size={18} className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} />
                    <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      {replyAttachments.length > 0 ? `${replyAttachments.length} file(s) selected` : 'Click to attach files'}
                    </span>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        if (e.target.files) {
                          setReplyAttachments(Array.from(e.target.files));
                        }
                      }}
                      className="hidden"
                    />
                  </label>

                  {/* Attachments Preview */}
                  {replyAttachments.length > 0 && (
                    <div className={`rounded-lg p-3 space-y-2 ${theme === 'dark' ? 'bg-slate-700/30' : 'bg-blue-50'
                      }`}>
                      <p className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                        Attachments ({replyAttachments.length})
                      </p>
                      <div className="space-y-1">
                        {replyAttachments.map((file, index) => (
                          <div
                            key={index}
                            className={`flex items-center justify-between px-3 py-2 rounded text-sm ${theme === 'dark'
                              ? 'bg-slate-700/50 text-gray-300'
                              : 'bg-white text-gray-700 border border-gray-200'
                              }`}
                          >
                            <span className="truncate flex items-center gap-2">
                              <FileText size={14} />
                              {file.name}
                            </span>
                            <button
                              onClick={() => {
                                setReplyAttachments(replyAttachments.filter((_, i) => i !== index));
                              }}
                              className={`p-1 rounded transition-colors ${theme === 'dark'
                                ? 'hover:bg-slate-600 text-gray-400'
                                : 'hover:bg-gray-100 text-gray-500'
                                }`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Send Button */}
                  <button
                    onClick={handleSendReply}
                    disabled={sendingReply || !replyMessage.trim()}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 font-medium rounded-lg transition-colors text-sm ${sendingReply || !replyMessage.trim()
                      ? theme === 'dark'
                        ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                  >
                    <Send size={16} />
                    {sendingReply ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Actions */}
          <div className="space-y-6">
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketDetailPage;
